import React from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Phone,
  MessageCircle,
  Pencil,
  MapPin,
  GraduationCap,
  Check,
  X,
  Activity as ActivityIcon,
  Clock,
  CalendarClock,
  FileText,
  StickyNote,
  Mail,
  MessageSquare,
  Send,
  Video,
  Users,
  ArrowUpRight,
  ArrowDownLeft,
} from "lucide-react";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";

const TYPE_ICON = {
  Call: Phone,
  WhatsApp: MessageCircle,
  Email: Mail,
  SMS: MessageSquare,
  Telegram: Send,
  "Online Meeting": Video,
  Visit: MapPin,
};

function fmtDateTime(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).toLowerCase();
  return { date, time, ts: d };
}

function fmtDate(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
}

function getTrainerName(row) {
  const person = row?.trainer?.organization_person?.person;
  const full = person?.full_name;
  const composed = `${person?.first_name || ""} ${person?.last_name || ""}`.trim();
  return full || composed || null;
}

function getActivityStatus(row) {
  const type = row?.activity_type;
  if (type === "Call") return row?.call_status || null;
  if (type === "Email") {
    if (row?.email_response_flag) return "Replied";
    if (row?.email_read_flag) return "Read";
    return "Sent";
  }
  if (type === "WhatsApp") {
    if (row?.whatsapp_response_flag) return "Replied";
    if (row?.whatsapp_read_flag) return "Read";
    return "Sent";
  }
  return null;
}

const STATUS_COLOR = {
  Attended: "var(--ems-green)",
  Replied: "var(--ems-green)",
  Read: "#0ea5b7",
  Sent: "var(--ems-muted)",
  Missed: "var(--ems-red)",
  Rejected: "var(--ems-red)",
  "No Answer": "var(--ems-amber)",
};

function colorMix(c, pct) {
  return `color-mix(in srgb, ${c} ${pct}%, var(--ems-surface))`;
}

/** Negative outcomes use a cross; positive stages use a tick. */
function isNegativeLeadStage(stage) {
  const name = String(stage?.lead_stage_name || stage?.lead_stage_short_name || "")
    .trim()
    .toLowerCase();
  return /^(unqualif|lost|spam)/.test(name);
}

function activityStageId(row) {
  return (
    row?.organization_ems_lead_stage_id ??
    row?.lead_stage?.organization_ems_lead_stage_id ??
    null
  );
}

function StagePill({ label, final: isFinal, lg }) {
  if (!label) return null;
  const c = isFinal ? "var(--ems-green)" : "var(--ems-primary)";
  return (
    <span
      className={"ems-stage-pill" + (lg ? " ems-snap-pill-lg" : "")}
      style={{ color: c, background: isFinal ? "var(--ems-green-tint)" : "var(--ems-primary-tint)" }}
    >
      <span className="ems-stage-dot" style={{ background: c }}></span>
      {label}
    </span>
  );
}

function StatusTag({ status }) {
  if (!status) return null;
  const c = STATUS_COLOR[status] || "var(--ems-muted)";
  return (
    <span className="ems-tag" style={{ color: c, background: colorMix(c, 13) }}>
      <span className="d" style={{ background: c }}></span>
      {status}
    </span>
  );
}

export default function LeadActivitySnapshot() {
  const navigate = useNavigate();
  const location = useLocation();
  const { leadId } = useParams();
  const { userData } = useAuthStore();
  const organizationId = userData?.organization?.organization_id;

  const [loading, setLoading] = React.useState(false);
  const [errorText, setErrorText] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [leadProfile, setLeadProfile] = React.useState(null);
  const [leadStages, setLeadStages] = React.useState([]);

  const leadIds = React.useMemo(() => {
    const list = location?.state?.leadIds;
    if (!Array.isArray(list)) return [];
    return list.map((id) => String(id).trim()).filter((id) => id && id !== "-");
  }, [location?.state?.leadIds]);

  const currentLeadIndex = React.useMemo(
    () => leadIds.findIndex((id) => id === String(leadId)),
    [leadIds, leadId]
  );
  const hasPrevLead = currentLeadIndex > 0;
  const hasNextLead = currentLeadIndex !== -1 && currentLeadIndex < leadIds.length - 1;

  const navigateToLeadAt = React.useCallback(
    (index) => {
      if (index < 0 || index >= leadIds.length) return;
      const targetLeadId = leadIds[index];
      navigate(`/lead-activities/snapshot/${encodeURIComponent(targetLeadId)}`, {
        state: { leadIds },
      });
    },
    [leadIds, navigate]
  );

  React.useEffect(() => {
    if (!organizationId || !leadId) return;
    let isActive = true;

    const load = async () => {
      setLoading(true);
      setErrorText("");
      try {
        const token = localStorage.getItem("token");
        const authHeaders = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };

        const [activitiesRes, stagesRes] = await Promise.all([
          axios.get(
            `${MAIN_URL}/api/ems/leads/${encodeURIComponent(String(leadId))}/activities`,
            { headers: authHeaders, params: { organization_id: organizationId } }
          ),
          axios
            .get(`${MAIN_URL}/api/ems/lead-stages`, {
              headers: authHeaders,
              params: { organization_id: organizationId, per_page: "all" },
            })
            .catch(() => null),
        ]);

        if (!isActive) return;

        const payload = activitiesRes?.data?.data;
        const leadObj = payload?.lead ?? null;
        const activities = Array.isArray(payload?.activities) ? payload.activities : [];
        setRows(activities);

        if (leadObj) {
          setLeadProfile(leadObj);
        } else {
          try {
            const leadRes = await axios.get(
              `${MAIN_URL}/api/ems/leads/${encodeURIComponent(String(leadId))}`,
              { headers: authHeaders, params: { organization_id: organizationId } }
            );
            setLeadProfile(
              leadRes?.data?.data?.lead || leadRes?.data?.data || leadRes?.data?.lead || null
            );
          } catch {
            setLeadProfile(null);
          }
        }

        const stagesList = Array.isArray(stagesRes?.data?.data) ? stagesRes.data.data : [];
        setLeadStages(
          [...stagesList].sort(
            (a, b) =>
              (a?.lead_stage_sequence_number ?? 0) - (b?.lead_stage_sequence_number ?? 0)
          )
        );
      } catch (err) {
        if (!isActive) return;
        setRows([]);
        setLeadProfile(null);
        setErrorText(
          err?.response?.data?.message || err?.message || "Failed to fetch lead snapshot."
        );
      } finally {
        if (!isActive) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isActive = false;
    };
  }, [organizationId, leadId]);

  const sortedActivities = React.useMemo(() => {
    if (!rows.length) return [];
    return [...rows].sort(
      (a, b) => new Date(b?.activity_datetime || 0).getTime() - new Date(a?.activity_datetime || 0).getTime()
    );
  }, [rows]);

  const leadInfo = leadProfile || {};
  const leadName = leadInfo?.person_full_name || "";
  const city = leadInfo?.city?.city_name || "";
  const state = leadInfo?.state?.state_name || "";
  const country = leadInfo?.country?.country_name || "";
  const locationLine = [city, country || state].filter(Boolean).join(", ") || "-";
  const programCategory = leadInfo?.training_program_category?.training_program_category_name || "-";
  const programName = leadInfo?.training_program?.training_program_name || "";
  const currentStageName = leadInfo?.lead_stage?.lead_stage_name || "";

  const currentStageIdx = React.useMemo(() => {
    if (!leadStages.length || !leadInfo?.lead_stage_id) return -1;
    return leadStages.findIndex(
      (s) => String(s?.organization_ems_lead_stage_id) === String(leadInfo.lead_stage_id)
    );
  }, [leadStages, leadInfo?.lead_stage_id]);

  const occurredStageIds = React.useMemo(() => {
    const ids = [];
    const push = (raw) => {
      const id = String(raw ?? "").trim();
      if (!id || id === "null" || id === "undefined") return;
      if (!ids.includes(id)) ids.push(id);
    };
    const newStage =
      leadStages.find((s) => /^new$/i.test(String(s?.lead_stage_name || "").trim())) ||
      leadStages[0];
    if (newStage) push(newStage.organization_ems_lead_stage_id);

    [...rows]
      .sort(
        (a, b) =>
          new Date(a?.activity_datetime || 0).getTime() -
          new Date(b?.activity_datetime || 0).getTime()
      )
      .forEach((row) => push(activityStageId(row)));

    push(leadInfo?.lead_stage_id ?? leadInfo?.lead_stage?.organization_ems_lead_stage_id);
    return ids;
  }, [leadStages, rows, leadInfo?.lead_stage_id, leadInfo?.lead_stage?.organization_ems_lead_stage_id]);

  const pipelineStages = React.useMemo(() => {
    const byId = new Map(
      leadStages.map((s) => [String(s?.organization_ems_lead_stage_id), s])
    );
    const occurredSet = new Set(occurredStageIds);
    const occurred = occurredStageIds.map((id) => byId.get(String(id))).filter(Boolean);
    const rest = leadStages.filter(
      (s) => !occurredSet.has(String(s?.organization_ems_lead_stage_id))
    );
    return { display: [...occurred, ...rest], occurredSet };
  }, [leadStages, occurredStageIds]);

  const isFinalStage = Boolean(
    currentStageIdx !== -1 &&
      /enroll/i.test(String(leadStages[currentStageIdx]?.lead_stage_name || ""))
  );

  const callCount = sortedActivities.filter((r) => r?.activity_type === "Call").length;
  const lastActivity = sortedActivities[0] || null;
  const lastFmt = lastActivity ? fmtDateTime(lastActivity.activity_datetime) : null;
  const daysInPipeline = React.useMemo(() => {
    if (!leadInfo?.lead_datetime) return null;
    const start = new Date(leadInfo.lead_datetime);
    if (Number.isNaN(start.getTime())) return null;
    return Math.max(0, Math.round((Date.now() - start.getTime()) / 86400000));
  }, [leadInfo?.lead_datetime]);

  if (loading) {
    return (
      <div className="ems-dashboard ems-fade-up">
        <div className="ems-loading-screen">
          <div className="ems-loading-card">Loading lead snapshot…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ems-dashboard ems-fade-up">
      <div className="ems-snap-topbar">
        <button type="button" className="ems-btn ems-ghost ems-snap-back" onClick={() => navigate("/leads")}>
          <ArrowLeft size={16} />
          Back
        </button>
        <div className="ems-snap-nav">
          <button
            type="button"
            className="ems-snap-navbtn"
            onClick={() => navigateToLeadAt(currentLeadIndex - 1)}
            disabled={!hasPrevLead}
            title="Previous lead"
          >
            <ChevronLeft size={17} />
          </button>
          <button
            type="button"
            className="ems-snap-navbtn"
            onClick={() => navigateToLeadAt(currentLeadIndex + 1)}
            disabled={!hasNextLead}
            title="Next lead"
          >
            <ChevronRight size={17} />
          </button>
        </div>
        <div className="ems-snap-titlewrap">
          <h1 className="ems-page-title">Lead Activity Snapshot</h1>
          <div className="ems-snap-id-sub">
            <span>
              Lead ID <b className="ems-tnum">{leadId}</b>
            </span>
            {leadName ? (
              <>
                <span className="ems-snap-dot"></span>
                <span>{leadName}</span>
              </>
            ) : null}
            {leadIds.length > 0 && currentLeadIndex !== -1 ? (
              <>
                <span className="ems-snap-dot"></span>
                <span className="ems-snap-pos ems-tnum">
                  {currentLeadIndex + 1} of {leadIds.length}
                </span>
              </>
            ) : null}
          </div>
        </div>
        <div className="ems-snap-actions">
          {leadInfo?.phone ? (
            <a className="ems-btn ems-ghost" href={`tel:${leadInfo.phone}`}>
              <Phone size={16} />
              Call
            </a>
          ) : null}
          {leadInfo?.phone ? (
            <a
              className="ems-btn ems-ghost"
              href={`https://wa.me/${String(leadInfo.phone).replace(/\D/g, "")}`}
              target="_blank"
              rel="noreferrer"
            >
              <MessageCircle size={16} />
              WhatsApp
            </a>
          ) : null}
          <button type="button" className="ems-btn ems-primary" onClick={() => navigate("/leads")}>
            <Pencil size={16} />
            Edit Lead
          </button>
        </div>
      </div>

      {errorText ? (
        <div className="ems-card" style={{ padding: "14px 18px", marginBottom: 18, color: "var(--ems-red)" }}>
          {errorText}
        </div>
      ) : null}

      {!errorText && !leadProfile ? (
        <div className="ems-card ems-empty">
          <div className="ems-empty-icon">
            <ActivityIcon size={26} />
          </div>
          No lead data found.
        </div>
      ) : (
        <>
          <div className="ems-card ems-snap-hero">
            <div className="ems-snap-hero-top">
              <div className="ems-snap-identity">
                <span
                  className="ems-t-avatar ems-snap-avatar"
                  style={{ background: "linear-gradient(140deg, var(--ems-chart-1), var(--ems-chart-3))" }}
                >
                  {initials(leadName)}
                </span>
                <div className="ems-snap-id-meta">
                  <div className="ems-snap-name">{leadName || "-"}</div>
                  <div className="ems-snap-loc">
                    <MapPin size={14} />
                    {locationLine}
                  </div>
                  <div className="ems-snap-pills">
                    <StagePill label={currentStageName} final={isFinalStage} lg />
                  </div>
                </div>
              </div>
              <div className="ems-snap-program">
                <span className="ems-snap-prog-label">Interested Program</span>
                <span className="ems-snap-prog-name">{programCategory}</span>
                {programName ? (
                  <span className="ems-snap-prog-track">
                    <GraduationCap size={14} />
                    {programName}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="ems-snap-fields">
              <div className="ems-snap-field">
                <span className="sl">Phone</span>
                <span className="sv ems-tnum">{leadInfo?.phone || <span className="ems-muted-dash">—</span>}</span>
              </div>
              <div className="ems-snap-field">
                <span className="sl">Alternate Phone</span>
                <span className="sv ems-tnum">
                  {leadInfo?.alternate_phone || <span className="ems-muted-dash">—</span>}
                </span>
              </div>
              <div className="ems-snap-field">
                <span className="sl">Email</span>
                <span className="sv">{leadInfo?.email || <span className="ems-muted-dash">—</span>}</span>
              </div>
              <div className="ems-snap-field">
                <span className="sl">Lead Source</span>
                <span className="sv">
                  {leadInfo?.lead_source?.lead_source_name || <span className="ems-muted-dash">—</span>}
                </span>
              </div>
              <div className="ems-snap-field">
                <span className="sl">Program Category</span>
                <span className="sv">{programCategory !== "-" ? programCategory : <span className="ems-muted-dash">—</span>}</span>
              </div>
              <div className="ems-snap-field">
                <span className="sl">Lead Came On</span>
                <span className="sv">{fmtDate(leadInfo?.lead_datetime)}</span>
              </div>
            </div>

            {pipelineStages.display.length > 0 && currentStageIdx !== -1 ? (
              <div className="ems-snap-pipeline">
                <span className="ems-snap-pipe-label">Pipeline Stage</span>
                <div className="ems-snap-track">
                  {pipelineStages.display.map((s, i) => {
                    const stageId = String(s?.organization_ems_lead_stage_id);
                    const negative = isNegativeLeadStage(s);
                    const reached = pipelineStages.occurredSet.has(stageId);
                    const isCurrent =
                      String(leadInfo?.lead_stage_id) === stageId;
                    const state = !reached
                      ? "todo"
                      : negative
                        ? isCurrent
                          ? "current-neg"
                          : "done-neg"
                        : isCurrent
                          ? "current"
                          : "done";
                    const showTick = reached && !negative;
                    const showCross = reached && negative;
                    const next = pipelineStages.display[i + 1];
                    const nextReached = Boolean(
                      next &&
                        pipelineStages.occurredSet.has(
                          String(next.organization_ems_lead_stage_id)
                        )
                    );
                    const lineReached = reached && nextReached;
                    const lineNeg =
                      lineReached &&
                      (isNegativeLeadStage(next) ||
                        (negative &&
                          String(leadInfo?.lead_stage_id) ===
                            String(next.organization_ems_lead_stage_id)));
                    return (
                      <React.Fragment key={s.organization_ems_lead_stage_id}>
                        <div className={"ems-snap-step " + state}>
                          <span className="ems-snap-step-dot">
                            {showTick ? (
                              <Check size={12} strokeWidth={3} />
                            ) : showCross ? (
                              <X size={12} strokeWidth={3} />
                            ) : null}
                          </span>
                          <span className="ems-snap-step-l">
                            {s.lead_stage_short_name || s.lead_stage_name}
                          </span>
                        </div>
                        {i < pipelineStages.display.length - 1 && (
                          <span
                            className={
                              "ems-snap-step-line " +
                              (lineReached ? (lineNeg ? "neg" : "done") : "")
                            }
                          ></span>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>

          <div className="ems-snap-kpis">
            <div className="ems-card ems-snap-kpi">
              <span className="ems-snap-kpi-ic">
                <ActivityIcon size={17} />
              </span>
              <div>
                <div className="ems-snap-kpi-v ems-tnum">{sortedActivities.length}</div>
                <div className="ems-snap-kpi-l">Total Activities</div>
              </div>
            </div>
            <div className="ems-card ems-snap-kpi">
              <span className="ems-snap-kpi-ic">
                <Phone size={17} />
              </span>
              <div>
                <div className="ems-snap-kpi-v ems-tnum">{callCount}</div>
                <div className="ems-snap-kpi-l">Calls Logged</div>
              </div>
            </div>
            <div className="ems-card ems-snap-kpi">
              <span className="ems-snap-kpi-ic">
                <Clock size={17} />
              </span>
              <div>
                <div className="ems-snap-kpi-v">{lastFmt ? lastFmt.date : "—"}</div>
                <div className="ems-snap-kpi-l">Last Contacted</div>
              </div>
            </div>
            <div className="ems-card ems-snap-kpi">
              <span className="ems-snap-kpi-ic">
                <CalendarClock size={17} />
              </span>
              <div>
                <div className="ems-snap-kpi-v ems-tnum">
                  {daysInPipeline !== null ? `${daysInPipeline}d` : "—"}
                </div>
                <div className="ems-snap-kpi-l">In Pipeline</div>
              </div>
            </div>
          </div>

          <div className="ems-sec-head ems-snap-actsec-head">
            <div className="ems-sec-title">
              <span className="ems-bar"></span>Activities
            </div>
            <div className="ems-snap-actcount">{sortedActivities.length} logged</div>
          </div>

          {sortedActivities.length ? (
            <div className="ems-snap-timeline">
              {sortedActivities.map((r, i) => {
                const f = fmtDateTime(r.activity_datetime);
                const TypeIcon = TYPE_ICON[r.activity_type] || Phone;
                const status = getActivityStatus(r);
                const trainerName = getTrainerName(r);
                const direction = r?.lead_direction || null;
                const resultingStage = r?.lead_stage?.lead_stage_name || null;
                return (
                  <div className="ems-snap-tl-item" key={r.organization_ems_lead_activity_id || i}>
                    <div className="ems-snap-tl-rail">
                      <span className="ems-snap-tl-node">
                        <TypeIcon size={15} />
                      </span>
                      {i < sortedActivities.length - 1 && <span className="ems-snap-tl-line"></span>}
                    </div>
                    <div className="ems-card ems-snap-actcard">
                      <div className="ems-snap-actcard-head">
                        <span className="ems-snap-idx">
                          {String(sortedActivities.length - i).padStart(2, "0")}
                        </span>
                        <div className="ems-snap-act-type">
                          <span className="ems-snap-act-typename">{r.activity_type || "-"}</span>
                          {direction ? (
                            <span className="ems-snap-act-dir">
                              {direction === "Incoming" ? (
                                <ArrowDownLeft size={13} strokeWidth={2.4} />
                              ) : (
                                <ArrowUpRight size={13} strokeWidth={2.4} />
                              )}
                              {direction}
                            </span>
                          ) : null}
                        </div>
                        <div className="ems-snap-act-when">
                          <span className="ems-snap-aw-date">{f ? f.date : "-"}</span>
                          <span className="ems-snap-aw-time ems-tnum">{f ? f.time : ""}</span>
                        </div>
                        <StatusTag status={status} />
                        <StagePill label={resultingStage} />
                      </div>

                      <div className="ems-snap-actcard-meta">
                        <div className="ems-snap-mkv">
                          <span className="k">Activity By</span>
                          <span className="v">{trainerName || <span className="ems-muted-dash">—</span>}</span>
                        </div>
                        <div className="ems-snap-mkv">
                          <span className="k">Direction</span>
                          <span className="v">{direction || <span className="ems-muted-dash">—</span>}</span>
                        </div>
                        <div className="ems-snap-mkv">
                          <span className="k">Resulting Stage</span>
                          <span className="v">{resultingStage || <span className="ems-muted-dash">—</span>}</span>
                        </div>
                        <div className="ems-snap-mkv">
                          <span className="k">Next Follow Up</span>
                          <span className="v">
                            {(() => {
                              const nf = fmtDateTime(r?.next_follow_up);
                              return nf
                                ? `${nf.date} ${nf.time}`
                                : <span className="ems-muted-dash">—</span>;
                            })()}
                          </span>
                        </div>
                      </div>

                      <div className="ems-snap-block">
                        <span className="ems-snap-block-l">
                          <FileText size={14} />
                          Activity Summary
                        </span>
                        {r?.activity_summary ? (
                          <p className="ems-snap-block-t">{r.activity_summary}</p>
                        ) : (
                          <p className="ems-snap-block-empty">No activity summary available.</p>
                        )}
                      </div>
                      <div className="ems-snap-block">
                        <span className="ems-snap-block-l">
                          <StickyNote size={14} />
                          Remarks
                        </span>
                        {r?.remarks ? (
                          <p className="ems-snap-block-t">{r.remarks}</p>
                        ) : (
                          <p className="ems-snap-block-empty">No remarks available.</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="ems-card ems-empty">
              <div className="ems-empty-icon">
                <Users size={26} />
              </div>
              No lead activities found for this lead.
            </div>
          )}
        </>
      )}
    </div>
  );
}
